import posthog from 'posthog-js';
import { supabase } from '../../lib/supabase';
import { getStoredPaxSession } from './session';
import { isImpersonating } from './impersonate';
import type { PaxEventName } from './events';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

const SESSION_ID_KEY = 'pax.session_id';

function getSessionId(): string {
    if (typeof window === 'undefined') return 'ssr';
    let id = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
        id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
        window.sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
}

export const initAnalytics = () => {
    if (typeof window !== 'undefined' && POSTHOG_KEY) {
        posthog.init(POSTHOG_KEY, {
            api_host: POSTHOG_HOST,
            autocapture: true,
            capture_pageview: false,
            session_recording: {
                maskAllInputs: true,
                maskTextSelector: '.sensitive-data'
            }
        });
    }
};

export const identifyUser = (id: string, email: string, name?: string) => {
    // While staff are impersonating, never identify them as the customer
    // they're viewing — that would taint the customer's PostHog profile
    // with staff-clicked events.
    if (isImpersonating()) return;
    if (POSTHOG_KEY) {
        posthog.identify(id, {
            email,
            name,
            platform: 'pax_portal'
        });
    }
};

export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
    // Suppress ALL analytics during impersonation — staff "viewing as
    // customer" sessions must not pollute customer activity dashboards.
    // We still want a record of impersonation activity for audit, but
    // that lives in pax_activity_events (server-side, on the
    // pax-impersonate-issue endpoint).
    if (isImpersonating()) return;
    if (POSTHOG_KEY) {
        posthog.capture(eventName, {
            ...properties,
            platform: 'pax_portal',
            timestamp: new Date().toISOString()
        });
    }
};

export const resetAnalytics = () => {
    if (POSTHOG_KEY) {
        posthog.reset();
    }
};

/**
 * Canonical pax event logger — emits to PostHog AND writes a row to
 * `pax_activity_events` so we can query activity in Reports without
 * leaving Supabase. Fire-and-forget; failures are swallowed to never
 * block the UI.
 */
export const logPaxActivity = (
    eventName: PaxEventName,
    props: Record<string, unknown> = {},
): void => {
    // Skip BOTH PostHog and Supabase activity writes during impersonation.
    // Customer dashboards must reflect what the customer actually did, not
    // what a staff member clicked while QA-ing the portal.
    if (isImpersonating()) return;
    trackEvent(eventName, props);

    void (async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            const magic = getStoredPaxSession();
            const customerId = magic?.customer.id ?? null;
            const userId = user?.id ?? null;
            if (!customerId && !userId) return;

            await supabase.from('pax_activity_events').insert({
                customer_id: customerId,
                user_id: userId,
                event_name: eventName,
                props,
                session_id: getSessionId(),
            });
        } catch {
            // best-effort logging — never throw to the caller
        }
    })();
};
